import type { ToolCallApprovalPayload } from "@/components/assistant/findActiveWriteApproval";
import {
  fetchAndMergePendingWriteApprovals,
  pollPendingWriteApprovalsUntilReady,
} from "@/components/assistant/fetchAndMergePendingWriteApprovals";
import type { UIMessage } from "ai";
import { useCallback, useEffect, type Dispatch, type SetStateAction } from "react";

type Args = {
  pendingUrl: string;
  status: string;
  messages: UIMessage[];
  isWriteToolName: (toolName: string) => boolean;
  normalizeProposalInput: (raw: unknown) => Record<string, unknown>;
  setApprovalByToolCallId: Dispatch<
    SetStateAction<Record<string, ToolCallApprovalPayload>>
  >;
  resolvedToolCallIds?: ReadonlySet<string>;
  /** When set and approval is not ready, poll /pending until runId is merged. */
  pendingToolCallId?: string | null;
  approvalReady?: boolean;
};

export function useMergePendingWriteApprovals({
  pendingUrl,
  status,
  messages,
  isWriteToolName,
  normalizeProposalInput,
  setApprovalByToolCallId,
  resolvedToolCallIds,
  pendingToolCallId,
  approvalReady,
}: Args): { mergePendingApprovalsFromServer: () => Promise<void> } {
  const mergePendingApprovalsFromServer = useCallback(async () => {
    await fetchAndMergePendingWriteApprovals({
      pendingUrl,
      isWriteToolName,
      normalizeProposalInput,
      setApprovalByToolCallId,
      resolvedToolCallIds,
    });
  }, [
    pendingUrl,
    isWriteToolName,
    normalizeProposalInput,
    setApprovalByToolCallId,
    resolvedToolCallIds,
  ]);

  useEffect(() => {
    void mergePendingApprovalsFromServer();
  }, [mergePendingApprovalsFromServer]);

  useEffect(() => {
    if (status === "ready") {
      void mergePendingApprovalsFromServer();
    }
  }, [status, messages, mergePendingApprovalsFromServer]);

  useEffect(() => {
    if (!pendingToolCallId || approvalReady) return;

    void pollPendingWriteApprovalsUntilReady({
      pendingUrl,
      toolCallId: pendingToolCallId,
      isWriteToolName,
      normalizeProposalInput,
      setApprovalByToolCallId,
      resolvedToolCallIds,
    });
  }, [
    pendingToolCallId,
    approvalReady,
    pendingUrl,
    isWriteToolName,
    normalizeProposalInput,
    setApprovalByToolCallId,
    resolvedToolCallIds,
  ]);

  return { mergePendingApprovalsFromServer };
}
